import { Menu, Transition } from "@headlessui/react";
import Icon, { IconName } from "./Icon";
import { useLayoutEffect, useState, useRef, RefObject } from "react";
import { createPortal } from "react-dom";

export interface SubMenuItem {
  id: string;
  text: string;
  icon?: IconName | (() => JSX.Element);
}

export interface DoubleDropdownProps {
  subMenuItems: SubMenuItem[];
  onSubMenuClick: (id: string) => void;
  subMenuPosition?: "left" | "right";
  type?: "project" | "projectGroup";
  triggerRef: RefObject<HTMLElement>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const SUBMENU_OFFSET_Y = -5;
const SUBMENU_Z_INDEX = 9999;

const DoubleDropdown = ({
  subMenuItems,
  onSubMenuClick,
  subMenuPosition,
  type = "projectGroup",
  triggerRef,
  onMouseEnter,
  onMouseLeave,
}: DoubleDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  // Tailwind w-48 / w-56 in px
  const width = type === "project" ? 192 : 224;

  useLayoutEffect(() => {
    if (!triggerRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const windowWidth = window.innerWidth;

    let position = subMenuPosition ?? "right";
    let left: number;

    if (position === "right") {
      left = triggerRect.right;
      if (left + width > windowWidth) {
        position = "left";
        left = triggerRect.left - width;
      }
    } else {
      left = triggerRect.left - width;
      if (left < 0) {
        position = "right";
        left = triggerRect.right;
      }
    }

    setCoords({
      top: triggerRect.top + SUBMENU_OFFSET_Y,
      left,
    });
  }, [subMenuPosition, triggerRef, width]);

  if (!coords) return null;

  const menu = (
    <div
      ref={dropdownRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: "fixed",
        top: coords.top,
        left: coords.left,
        zIndex: SUBMENU_Z_INDEX,
      }}
      className={`${
        type === "project" ? "w-48" : "w-56"
      } bg-white border border-gray-200 rounded-md shadow-lg focus:outline-none font-normal`}
    >
      <Transition
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items>
          {subMenuItems.map((subItem) => (
            <Menu.Item key={subItem.id}>
              <button
                className={`w-full ${
                  type === "project" ? "px-2 pt-1.5 pb-1" : "px-3 py-1.5"
                } text-sm hover:bg-gray-100 flex items-center`}
                onClick={() => onSubMenuClick(subItem.id)}
              >
                {typeof subItem.icon === "string" ? (
                  <Icon icon={subItem.icon} className="h-5 w-5 stroke-green-500 mr-2" />
                ) : (
                  <div className="w-5 h-5 mr-2">{subItem.icon()}</div>
                )}
                {subItem.text}
              </button>
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </div>
  );

  return createPortal(menu, document.body);
};

export default DoubleDropdown;
